import React, { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import LockRoundedIcon from '@mui/icons-material/LockRounded';

import { fetchJson } from '../apiBase';

const DEFAULT_PRIVACY_NOTICE_URL = 'https://www.dandu.com.tr/legal/privacy.html';

async function readJsonResponse(response) {
  if (!response.ok) {
    throw new Error(`İstek başarısız oldu (${response.status}).`);
  }
  return response.json();
}

const PrivacyNoticeGate = ({ children }) => {
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [acknowledging, setAcknowledging] = useState(false);
  const [actionError, setActionError] = useState('');
  const displayedVersionRef = useRef(null);

  const loadNotice = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await fetchJson('/auth/privacy-notice', { dedupe: false });
      setNotice(await readJsonResponse(response));
    } catch {
      setLoadError('KVKK aydınlatma bilgisi yüklenemedi. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotice();
  }, [loadNotice]);

  useEffect(() => {
    if (!notice?.requires_acknowledgement) return;
    if (displayedVersionRef.current === notice.notice_version) return;

    displayedVersionRef.current = notice.notice_version;
    fetchJson('/auth/privacy-notice/displayed', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })
      .then(readJsonResponse)
      .catch(() => {
        displayedVersionRef.current = null;
      });
  }, [notice]);

  const acknowledge = async () => {
    setAcknowledging(true);
    setActionError('');
    try {
      const response = await fetchJson('/auth/privacy-notice/acknowledge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      setNotice(await readJsonResponse(response));
    } catch {
      setActionError('İşlem kaydedilemedi. Lütfen tekrar deneyin.');
    } finally {
      setAcknowledging(false);
    }
  };

  const requiresAcknowledgement = Boolean(notice?.requires_acknowledgement);
  const showDialog = Boolean(loadError) || requiresAcknowledgement;

  return (
    <>
      {children}
      <Dialog
        open={showDialog}
        disableEscapeKeyDown
        fullWidth
        maxWidth="sm"
        aria-labelledby="privacy-notice-title"
      >
        <DialogTitle id="privacy-notice-title" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 900 }}>
          <LockRoundedIcon color="primary" />
          Kişisel Verilerin Korunması
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress aria-label="KVKK aydınlatma bilgisi yükleniyor" />
            </Box>
          ) : null}

          {loadError ? <Alert severity="error">{loadError}</Alert> : null}

          {!loading && !loadError && requiresAcknowledgement ? (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Typography>
                DANDU’yu kullanırken size ve kullanım yetkiniz kapsamındaki öğrencilere ait bazı
                kişisel verileri, hizmetlerimizi sunabilmek ve öğrencilerin gelişimini takip
                edebilmek amacıyla işliyoruz.
              </Typography>
              <Typography>
                Kişisel verilerinizi nasıl işlediğimiz, kimlerle paylaştığımız ve KVKK kapsamındaki
                haklarınız hakkında detaylı bilgiye KVKK Aydınlatma Metni üzerinden ulaşabilirsiniz.
              </Typography>
              <Link
                href={notice.document_url || DEFAULT_PRIVACY_NOTICE_URL}
                target="_blank"
                rel="noreferrer"
                sx={{ fontWeight: 800, width: 'fit-content' }}
              >
                KVKK Aydınlatma Metni →
              </Link>
              {actionError ? <Alert severity="error">{actionError}</Alert> : null}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {loadError ? (
            <Button variant="contained" onClick={loadNotice} disabled={loading}>
              Tekrar dene
            </Button>
          ) : null}
          {!loading && !loadError && requiresAcknowledgement ? (
            <Button
              variant="contained"
              onClick={acknowledge}
              disabled={acknowledging}
              startIcon={acknowledging ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ fontWeight: 800, textTransform: 'none' }}
            >
              Bilgilendirmeyi gördüm, devam et
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PrivacyNoticeGate;
